import { dcloudApi } from '../api';
import { CONNECTIONS_PATH } from '../../api/paths';
import i18n from '../../i18n';
import {
  addDerivedPropertiesToConnections,
  updateAllConnectionsTotalValue,
} from '../diagram/utils';
import { diagramApi } from '../diagram/api';
import { TAGS } from '../api/constants';
import { topologiesSelectors } from '../topologies/slice';
import { store } from '../store';

export const connectionsApi = dcloudApi.injectEndpoints({
  endpoints: (build) => ({
    createConnection: build.mutation<
      DiagramConnection,
      CreateConnectionPayload
    >({
      async onQueryStarted(
        { topology: { uid } },
        { dispatch, queryFulfilled },
      ) {
        const topology = topologiesSelectors.getTopologyToEdit(
          store.getState(),
        );
        try {
          const { data: connection } = await queryFulfilled;
          dispatch(
            diagramApi.util.updateQueryData(
              'getDiagram',
              uid,
              (response: DiagramResponse) =>
                addDerivedPropertiesToConnections({
                  connection,
                  diagramResponse: response,
                }),
            ),
          );
          updateAllConnectionsTotalValue(connection);
        } catch {
          dispatch(diagramApi.util.invalidateTags([TAGS.DIAGRAM]));
        } finally {
          if (topology?.inSyncWithDcloud) {
            dispatch(diagramApi.util.invalidateTags([TAGS.TOPOLOGY]));
          }
        }
      },
      query: (payload) => ({
        data: payload,
        errorMessage: i18n.t('diagram.add.error'),
        method: 'post',
        url: CONNECTIONS_PATH,
      }),
    }),
    deleteConnection: build.mutation<undefined, string>({
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        const topology = topologiesSelectors.getTopologyToEdit(
          store.getState(),
        );
        try {
          await queryFulfilled;
        } catch {
          dispatch(diagramApi.util.invalidateTags([TAGS.DIAGRAM]));
        } finally {
          if (topology?.inSyncWithDcloud) {
            dispatch(diagramApi.util.invalidateTags([TAGS.TOPOLOGY]));
          }
        }
      },
      query: (connectionUid) => ({
        errorMessage: i18n.t('diagram.delete.error'),
        method: 'delete',
        url: `${CONNECTIONS_PATH}/${connectionUid}`,
      }),
    }),
    updateConnection: build.mutation<Connection, UpdateConnectionPayload>({
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        const topology = topologiesSelectors.getTopologyToEdit(
          store.getState(),
        );
        try {
          await queryFulfilled;
        } catch {
          dispatch(diagramApi.util.invalidateTags([TAGS.DIAGRAM]));
        } finally {
          if (topology?.inSyncWithDcloud) {
            dispatch(diagramApi.util.invalidateTags([TAGS.TOPOLOGY]));
          }
        }
      },
      query: ({ display, connectionUid }) => ({
        data: { display },
        errorMessage: i18n.t('diagram.update.error'),
        method: 'patch',
        url: `${CONNECTIONS_PATH}/${connectionUid}`,
      }),
    }),
  }),
});

export const {
  useCreateConnectionMutation,
  useDeleteConnectionMutation,
  useUpdateConnectionMutation,
} = connectionsApi;
